import { AppShellInsets } from '~/features/app/useAppShellInsets';

import { zIndices } from '../../tokens/zIndices';
import { SharedPanelProps } from '../../types/SharedPanelProps';
import { RIGHT_SPACE } from '../PageSidebar/constants';

/** Spacing tokens resolve a bare number as a scale index, so px insets have to
 *  arrive as a string. */
const toPx = (value: number | undefined) =>
  value === undefined ? undefined : `${value}px`;

export const getPanelPosition = ({
  mode,
  maximizeInsets,
  offsetTop,
  position,
  hideRailGutter = false,
  fitShellContent = false,
  stickyHeaderOffset,
  useShellStickyOffset = false,
}: {
  mode: SharedPanelProps['mode'];
  maximizeInsets?: AppShellInsets;
  offsetTop: SharedPanelProps['offsetTop'];
  position: SharedPanelProps['position'];
  hideRailGutter?: SharedPanelProps['hideRailGutter'];
  fitShellContent?: SharedPanelProps['fitShellContent'];
  stickyHeaderOffset?: number;
  useShellStickyOffset?: boolean;
}) => {
  let positionStyle = {};
  if (mode === 'floating') {
    positionStyle = {
      bottom: 4,
      right: 4,
      top: offsetTop ? `calc(80px + ${offsetTop}px)` : '80px',
    };
  } else if (mode === 'floating-full-width') {
    // Full viewport by default (old top-nav layout); the sidebar-nav layout
    // provides insets so this fills the content area instead.
    positionStyle = {
      bottom: toPx(maximizeInsets?.bottom) ?? 3,
      left: toPx(maximizeInsets?.left) ?? 3,
      right: toPx(maximizeInsets?.right) ?? 3,
      top: toPx(maximizeInsets?.top) ?? '76px',
    };
  } else if (position === 'right') {
    positionStyle = {
      bottom: mode === 'inline' ? '64px' : 0,
      right: 0,
      top: offsetTop ? `calc(64px + ${offsetTop}px)` : '64px',
    };
  } else if (position === 'left') {
    positionStyle = {
      bottom: mode === 'inline' ? '64px' : 0,
      left: 0,
      top: offsetTop ? `calc(64px + ${offsetTop}px)` : '64px',
    };
  } else if (mode === 'fullscreen') {
    positionStyle = {
      top: 0,
      right: 0,
    };
  } else if (mode === 'floating-sidepanel' && useShellStickyOffset) {
    const top = (stickyHeaderOffset ?? 76) + 5;
    positionStyle = {
      bottom: 3,
      right: hideRailGutter ? 4 : RIGHT_SPACE,
      top: offsetTop ? `calc(${top}px + ${offsetTop}px)` : `${top}px`,
    };
  } else if (mode === 'floating-sidepanel') {
    const right = hideRailGutter ? 4 : `calc(12px + ${RIGHT_SPACE})`;
    positionStyle = {
      bottom: fitShellContent ? 'var(--app-shell-bottom-inset, 12px)' : 3,
      right: fitShellContent ? 'var(--app-shell-rail-inset, 79px)' : right,
      top: fitShellContent
        ? 'var(--app-shell-rail-top, var(--app-shell-sticky-offset, 76px))'
        : '76px',
    };
  }

  return {
    position: 'fixed',
    ...positionStyle,
  };
};

export const basePanelWrapperStyle = ({
  mode,
  zIndex = zIndices.panel,
  expandWidth,
}: {
  mode: SharedPanelProps['mode'];
  zIndex?: string;
  expandWidth?: number | string;
}) => ({
  boxShadow: mode === 'inline' ? 'none' : 'raised',
  zIndex,
  borderRadius: 'ml',
  ...(expandWidth !== undefined && {
    width: expandWidth,
  }),
});
